
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Download,
  AlertCircle,
  Clock,
  CheckCircle,
  RefreshCw,
  FileText,
  Loader2
} from 'lucide-react';
import { useRealtimeExportStatus } from '@/hooks/useRealtimeExportStatus';
import { downloadExportFile, manualPrepareStreaming, triggerCompletionDetection } from '@/utils/organization/backgroundExport';
import {
  getDisplayErrorMessage,
  getDisplayExportStatus,
  getDisplayStepMessage,
  isExportFinalizing,
  isExportReady,
} from '@/utils/organization/exportStatusDisplay';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ChatExportStatusProps {
  exportInfo: {
    export_id: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    table_name: string;
    schema_name?: string;
    connection_id?: string;
    platform?: string;
    organisation_id?: string;
  };
}

const ChatExportStatus: React.FC<ChatExportStatusProps> = ({ exportInfo }) => {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [combiningStartTime, setCombiningStartTime] = useState<number | null>(null);
  const [nudgeTriggered, setNudgeTriggered] = useState(false);
  const [preparingDownload, setPreparingDownload] = useState(false);
  // Use the realtime hook to get live status updates and refetch
  const { status: liveStatus, error: statusError, refetch } = useRealtimeExportStatus({
    exportId: exportInfo.export_id,
    maxMonitoringDuration: 10 * 60 * 1000, // 10 minutes for chat context
    onStatusChange: (s) => {
      if ((s as any).status === 'combining' && !combiningStartTime) {
        setCombiningStartTime(Date.now());
        setNudgeTriggered(false);
      }
      if ((s as any).status !== 'combining') {
        setCombiningStartTime(null);
        setNudgeTriggered(false);
      }
    }
  });

  // Use live status if available, fall back to initial export info
  const realtimeStatus = liveStatus;
  const currentStatus = liveStatus || {
    ...exportInfo,
    current_step: undefined,
    actual_row_count: undefined,
    processing_duration_ms: undefined,
    download_url: undefined,
    error_message: undefined
  };
  const displayStatus = getDisplayExportStatus(currentStatus as any);
  const isReady = isExportReady(currentStatus as any);
  const isFinalizing = isExportFinalizing(currentStatus as any);

  // Auto-nudge if combining for too long (reduced to 20s)
  React.useEffect(() => {
    if ((realtimeStatus?.status === 'combining' || isFinalizing) && !realtimeStatus?.download_url && combiningStartTime && !nudgeTriggered) {
      const elapsed = Date.now() - combiningStartTime;
      if (elapsed > 20000) { // 20 seconds
        console.log('Export combining for >20s, triggering completion detection');
        triggerCompletionDetection();
        setNudgeTriggered(true);
      }
    }
  }, [realtimeStatus, combiningStartTime, nudgeTriggered, isFinalizing]);

  const handleManualPrepare = async () => {
    if (!exportInfo.export_id || preparingDownload) return;

    setPreparingDownload(true);
    try {
      console.log('Manually preparing download for', exportInfo.export_id);
      await manualPrepareStreaming(exportInfo.export_id, realtimeStatus?.chunk_count || 1);
    } catch (error) {
      console.error('Manual prepare failed:', error);
    } finally {
      setPreparingDownload(false);
    }
  };

  const handleDownload = async () => {
    if (!currentStatus.download_url) return;

    setIsDownloading(true);
    try {
      // Get organization name using organisation_id
      const { data: orgData } = await supabase
        .from('organisations')
        .select('name')
        .eq('id', currentStatus.organisation_id)
        .maybeSingle();

      const orgName = orgData?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'export';
      const filename = `${orgName}_${currentStatus.table_name}.csv`;

      await downloadExportFile(currentStatus.download_url, filename, undefined, exportInfo.export_id);

      toast({
        title: "Download Started",
        description: "Your CSV file download has started.",
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: error instanceof Error ? error.message : "Failed to download file",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusIcon = () => {
    switch (displayStatus) {
      case 'queued':
        return <Clock className="h-4 w-4" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'finalizing':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusColor = () => {
    switch (displayStatus) {
      case 'queued':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'finalizing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressPercentage = () => {
    if (isReady) return 100;
    if (currentStatus.status === 'failed') return 0;
    if (currentStatus.status === 'queued') return 0;

    // For processing status, show estimated progress
    if ('chunks_completed' in currentStatus && 'chunk_count' in currentStatus &&
      currentStatus.chunks_completed && currentStatus.chunk_count) {
      return Math.round((currentStatus.chunks_completed / currentStatus.chunk_count) * 90); // Reserve 10% for final steps
    }

    return 0; // Default processing percentage
  };

  // Filter out retry steps from display
  const shouldShowStep = (step?: string) => {
    if (!step) return false;

    const retrySteps = [
      'retrying_chunk_generation',
      'retry_chunk',
      'chunk_retry_attempt'
    ];

    return !retrySteps.some(retryStep =>
      step.toLowerCase().includes(retryStep.toLowerCase())
    );
  };

  const getStepDescription = () => {
    if (!shouldShowStep(currentStatus.current_step)) return 'Processing data...';
    const displayStep = getDisplayStepMessage(currentStatus.current_step);
    if (!displayStep) return 'Processing data...';

    const stepDescriptions: Record<string, string> = {
      'INITIALIZING': 'Initializing export...',
      'CONNECTED_TO_DB': 'Connected to database',
      'TABLE_VALIDATED': 'Validating table structure',
      'EXECUTING_QUERY': 'Retrieving data',
      'QUERY_COMPLETED': 'Data retrieval completed',
      'DATA_PROCESSED': 'Processing data',
      'CSV_GENERATED': 'Generating CSV file',
      'combining_chunks': 'Finalizing export file',
      'download_ready': 'Ready for download',
      'COMPLETED': 'Export completed'
    };

    return stepDescriptions[currentStatus.current_step || ''] || displayStep;
  };
  const displayError = getDisplayErrorMessage(currentStatus.error_message);

  // Handle export not found case
  if (statusError && statusError.includes('Export not found')) {
    return (
      <div className="bg-gray-50 border rounded-lg p-3 max-w-md">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-700">
            Export: {exportInfo.table_name}
          </span>
        </div>
        <div className="text-xs text-amber-600 mb-2 p-2 bg-amber-50 rounded">
          Export is no longer available - it may have been cleaned up or expired.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border rounded-lg p-3 max-w-md">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm font-medium">
            Export: {currentStatus.table_name}
            {exportInfo.platform && (
              <span className="text-xs text-gray-500 ml-1">({exportInfo.platform})</span>
            )}
          </span>
        </div>
        <Badge className={getStatusColor()}>
          {displayStatus === 'combining' ? 'PROCESSING' : displayStatus.toUpperCase()}
        </Badge>
      </div>

      {/* Progress bar for processing exports */}
      {(currentStatus.status === 'processing' || currentStatus.status === 'combining' || isFinalizing) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {currentStatus.status === 'combining' || isFinalizing ? 'Finalizing export...' : getStepDescription()}
            </span>
            <span className="font-medium">{Math.round(getProgressPercentage())}%</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />

          {(currentStatus.status === 'combining' || isFinalizing) && !realtimeStatus?.download_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualPrepare}
              disabled={preparingDownload}
              className="w-full mt-2"
            >
              {preparingDownload ? 'Preparing...' : 'Prepare download'}
            </Button>
          )}
        </div>
      )}

      {/* Row count display */}
      {currentStatus.actual_row_count !== undefined && currentStatus.actual_row_count !== null && (
        <div className="text-xs text-gray-600 mb-2">
          {currentStatus.actual_row_count.toLocaleString()} rows exported
        </div>
      )}

      {/* Error message for failed exports (hide connection pool errors) */}
      {currentStatus.status === 'failed' && displayError &&
        !displayError.includes('remaining connection slots') &&
        !displayError.includes('Database temporarily busy') && (
          <div className="text-xs text-red-600 mb-2 p-2 bg-red-50 rounded">
            {displayError}
          </div>
        )}

      {/* Download button for completed exports */}
      {isReady && currentStatus.download_url && (
        <Button
          data-testid="chat-export"
          size="sm"
          onClick={handleDownload}
          disabled={isDownloading}
          className="w-full"
        >
          <Download className="h-4 w-4 mr-2" />
          {isDownloading ? 'Downloading...' : 'Download CSV'}
        </Button>
      )}

    </div>
  );
};

export default ChatExportStatus;